import { store } from '@/app/store';

// ---------------------------------------------------------------------------
// MW light checkout — /checkout.json (middleware 0.7.18, ticket #91).
//
// Deliberately NOT routed through apiOperation: that helper toasts the raw
// body of every non-OK answer, and this endpoint answers with MACHINE codes
// ("no_tv", "tv_update_needed", …) that are meant to be translated into the
// operator-facing sentences of §3.7 before anyone sees them. Several of them
// are also expected answers rather than failures — every 409 but
// project_disabled carries the room's current row in `result`, which the
// caller applies instead of showing an error — so the toast has to stay the
// caller's decision.
//
// The envelope on a refusal is
//   {"errors":[{"code":<HTTP status>,"error":"<machine code>","result":<row>}]}
// which is NOT the house {code:0} envelope: `code` here is the HTTP status.
// ---------------------------------------------------------------------------

export const checkoutUrl = "/checkout.json";

// §5.1 — what the button may do. Anything unknown is treated as "not ready".
export type CheckoutTv = "none" | "update_needed" | "cannot_clear" | "ready";

export interface CheckoutLast {
  at:					number;		// unix seconds
  result:				string;		// "started" | "done" | "failed"
}

export interface CheckoutRequest {
  id:					string;		// 32 lowercase hex — needed for Cancel
  state:				string;		// §5.2
  requested_at:			number;
  expires_at:			number;
  acked_at?:			number;
  ended_at?:			number;
  reason?:				string;		// only for `failed` — §5.4
}

export interface CheckoutRoom {
  room_no:				string;
  name:					string;		// raw; roomDisplayName() decides whether to show it
  tv:					string;
  last_checkout:		CheckoutLast | null;
  checkout:				CheckoutRequest | null;
}

// §3.8 — the same row minus room_no/name, as admin room reads carry it under
// `mw_checkout`. Read-only: the server ignores it in a POST /rooms.json body,
// which is why copyProjectRoom deliberately does not carry it.
export interface RoomMwCheckout {
  tv:					string;
  last_checkout:		CheckoutLast | null;
  checkout:				CheckoutRequest | null;
}

export interface CheckoutProject {
  id:					string;
  name:					string;
  enabled:				boolean;
}

export interface CheckoutTable {
  project:				CheckoutProject;
  server_time:			number;		// the SERVER's clock — hotel tablets are often wrong
  can_checkout:			boolean;
  // §4.5 — the System settings Checkout switch, so the page can say why every
  // Checkout is disabled. Missing (a server before 0.7.18ubuntu3) reads as on.
  checkout_enabled:		boolean;
  // #91 §0.8 — whether the Checkout is on FOR THIS HOTEL: its own value when it
  // has one, else System settings. The pages decide "off" from this field, never
  // from checkout_enabled alone. Missing (before 0.7.18ubuntu6) reads as
  // checkout_enabled, which was the only switch there.
  project_checkout_enabled:	boolean;
  rooms:				CheckoutRoom[];
}

export interface CheckoutError {
  status:				number;		// HTTP status, 0 for a network failure
  code:					string;		// the machine code, e.g. "no_tv"
  message:				string;		// the sentence to show (§3.7)
  retryAfter?:			number;		// seconds, from Retry-After
  row?:					CheckoutRoom | null;	// every 409 but project_disabled carries one
}

export type CheckoutReadResult =
  | { kind: "ok";		table: CheckoutTable }
  | { kind: "error";	error: CheckoutError };

export type CheckoutPostResult =
  | { kind: "ok";		created: boolean; room: CheckoutRoom }
  | { kind: "error";	error: CheckoutError };

// §4.6 — a press while the Checkout is switched off in System settings. A 409
// like the others: it carries the room's row in `result`.
export const checkoutDisabledCode = "checkout_disabled";

export type CheckoutAction = "checkout" | "cancel";

// ---------------------------------------------------------------------------
// §5.2 / §5.3 / §5.4 / §5.5 / §5.6 — the texts.
//
// Kept here, in English, rather than as i18n keys: this is the exact wording
// the handoff specifies, it is what the unit tests assert (§7), and several of
// these mirror a server sentence that is shown as is. Page chrome (column
// headers, button captions) does go through i18next.
// ---------------------------------------------------------------------------

function pad2(n: number): string {
  return n < 10 ? "0" + n : "" + n;
}

// "24.09. 10:33" — day, month, then the time. The year is left out on purpose:
// a checkout older than a season is not what the column is read for.
export function formatCheckoutTime(ts?: number | null): string {
  if (	!ts ||
		ts <= 0 ||
		!isFinite(ts)
	 )
    return "";

  const d = new Date(ts * 1000);

  return	pad2(d.getDate()) + "." +
			pad2(d.getMonth() + 1) + ". " +
			pad2(d.getHours()) + ":" +
			pad2(d.getMinutes());
}

const reasonTexts: { [reason: string]: string } = {
  wipe_error:			"the TV could not start clearing",
  busy:					"the TV was already clearing",
  unsupported_platform:	"this kind of TV cannot clear apps",
  storage_not_cleared:	"some apps were not cleared",
  other:				"another problem on the TV",
};

// §5.4. An unknown reason is dropped rather than printed raw — the state text
// already says the checkout failed, and a machine token adds nothing.
export function checkoutReasonText(reason?: string): string {
  if (!reason)
    return "";

  return reasonTexts[reason] || "";
}

// §5.2 — the Status column. A row that never had a request answers "", so the
// caller can print nothing at all.
export function checkoutStatusText(checkout?: CheckoutRequest | null): string {
  if (	!checkout ||
		!checkout.state
	 )
    return "";

  const at = (ts?: number) => formatCheckoutTime(ts);

  switch (checkout.state) {
  case "pending":
    return "Waiting for TV since " + at(checkout.requested_at);

  case "waiting_no_tv":
    return "Waiting: no active TV in this room";

  // `in_progress` is gone from the server (§0.2); should an old row still carry
  // it, it falls through to "Unknown status (…)" like any other unknown state.

  case "started":
    // §0.3 — a plain status, never a warning. On Philips, Samsung and LG this
    // is where a checkout normally ENDS: they restart to clear and cannot
    // report how it finished, so nothing here may read like a fault.
    return "Checkout started on " + at(checkout.acked_at || checkout.requested_at);

  case "done":
    return "TV reported done at " + at(checkout.ended_at || checkout.requested_at);

  case "failed": {
    const reason = checkoutReasonText(checkout.reason);
    return "Failed at " + at(checkout.ended_at || checkout.requested_at) +
           (reason ? ": " + reason : "");
  }

  case "unsupported":
    return "This TV could not clear apps";

  case "cancelled":
    return "Cancelled";

  case "expired":
    return "The TV did not answer in 7 days";

  case "tv_replaced":
    return "Not sent: the TV was replaced";

  default:
    return "Unknown status (" + checkout.state + ")";
  }
}

// §5.3. Only MW light checkouts a TV confirmed reach this field, so "Never" is
// the honest answer for a hotel whose TVs only ever cleared at reboot.
export function lastCheckoutText(last?: CheckoutLast | null): string {
  if (	!last ||
		!last.at
	 )
    return "Never";

  return formatCheckoutTime(last.at) + " · " + (last.result || "");
}

// §5.5 — the second line under the room number, or "" when the stored name is
// not a real name. "App Activated" is the placeholder the server gives a room
// that a TV activated by itself, so it is never a name.
export function roomDisplayName(roomNo: string, name?: string): string {
  const n = (name || "").trim();

  if (n === "")
    return "";

  if (n === (roomNo || "").trim())
    return "";

  if (n === "App Activated")
    return "";

  return n;
}

// "101", or "101 (Suite Eiger)" when there is a real name.
export function roomLabel(roomNo: string, name?: string): string {
  const real = roomDisplayName(roomNo, name);

  return real ? roomNo + " (" + real + ")" : roomNo;
}

// §5.6. One dialog, one sentence, no room for doubt about what is lost.
export function confirmCheckoutText(roomNo: string, name?: string): string {
  return "Clear ALL app data on the TV in room " + roomLabel(roomNo, name) +
         "? Netflix, YouTube and all other apps will be logged out. " +
         "This cannot be undone.";
}

// §5.1 (rev 4) — the Checkout button exists ONLY for a TV that reported its
// capabilities. Every other value gets no button at all, not a disabled one.
export function tvHasCheckout(tv?: string): boolean {
  return tv === "ready";
}

// §5.1 — the text that stands in place of the button. "" for a ready TV, which
// has a button instead.
//
// `update_needed` (no caps, or no checkout.v1) and `cannot_clear` (checkout.v1
// without checkout.clear) deliberately share one sentence: staff cannot tell
// them apart and do not need to. Support can, from the room's `last_caps`
// field in the room payload - it is no longer shown in the room view, so read
// it from the API rather than looking for it on the page.
export const deviceUnsupportedText = "Device not supported or app update required";

export function tvUnavailableText(tv?: string): string {
  if (tvHasCheckout(tv))
    return "";

  // "No TV" is only for a room with no active TV at all — there is no device
  // there to call unsupported. An UNKNOWN value from a future server is not
  // that, so it falls through to the generic text rather than claiming the
  // room is empty.
  if (tv === "none")
    return "No TV";

  return deviceUnsupportedText;
}

// §6.2 — the two states that mean "the request still waits for the TV", which
// is what both the fast refresh (§6.7) and Cancel key on. `started` is not one
// of them: it refreshes at the normal pace (§0.2).
export function isActiveCheckoutState(state?: string): boolean {
  return	state === "pending" ||
			state === "waiting_no_tv";
}

// §5.1 + §2.4. `can_checkout` is the caller's right plus the hotel's switch
// plus the server's emergency brake, so it gates every row at once.
export function canCheckoutRow(row: CheckoutRoom | null, canCheckout: boolean): boolean {
  if (	!row ||
		!canCheckout
	 )
    return false;

  if (!tvHasCheckout(row.tv))
    return false;

  // While one request waits, Checkout is replaced by Cancel — pressing it
  // again would only return the same request (§5.2).
  return !isActiveCheckoutState(row.checkout ? row.checkout.state : undefined);
}

// §3.2 — what Cancel is gated on. `can_checkout` is false while the Checkout
// is off for the hotel, yet a request that was already waiting must stay
// cancellable then (the server accepts that cancel), so the Checkout being off
// opens Cancel by itself. A switched-off hotel still closes it.
//
// #91 §0.8 — the second argument is the HOTEL's state
// (`project_checkout_enabled`), not the System settings switch.
export function cancelAllowed(canCheckout: boolean, projectCheckoutEnabled: boolean, projectEnabled: boolean): boolean {
  if (!projectEnabled)
    return false;

  return canCheckout || !projectCheckoutEnabled;
}

// §2.4 — Cancel only while the TV has not taken the request yet, and only for
// the request the row shows (its id goes in the body). `canCancel` is the
// page-level gate, from cancelAllowed().
export function canCancelRow(row: CheckoutRoom | null, canCancel: boolean): boolean {
  if (	!row ||
		!row.checkout ||
		!canCancel
	 )
    return false;

  return	row.checkout.state === "pending" ||
			row.checkout.state === "waiting_no_tv";
}

export const checkoutFastRefreshMs	= 5000;
export const checkoutSlowRefreshMs	= 30000;

// §5.7. `panel` is the admin room panel, which has no idle refresh at all —
// nothing changes there unless a checkout is in flight. 0 means "do not poll".
export function refreshIntervalMs(rows: CheckoutRoom[], panel?: boolean): number {
  const active = (rows || []).some((r) =>
    isActiveCheckoutState(r && r.checkout ? r.checkout.state : undefined)
  );

  if (active)
    return checkoutFastRefreshMs;

  return panel ? 0 : checkoutSlowRefreshMs;
}

// ---------------------------------------------------------------------------
// §3.7 — machine code to sentence.
// ---------------------------------------------------------------------------

const genericBadRequest = "The server did not accept this request.";

// §6.8 — the banner while the Checkout is switched off, and the 409 text.
export const checkoutSwitchedOffText = "Checkout is switched off on this server.";
// #91 §0.8 — off for this hotel while System settings is on.
export const checkoutSwitchedOffHotelText = "Checkout is switched off for this hotel.";
// #91 §0.8 — a 409 checkout_disabled whose latest read still says "on".
export const checkoutSwitchedOffChangedText = "Checkout is switched off.";

// The two inputs of the "off" rule, as the pages hold them.
export interface CheckoutSwitchState {
  checkout_enabled:			boolean;	// System settings
  project_checkout_enabled:	boolean;	// this hotel
}

// #91 §0.8 / §3.2 — the Checkout is off for the hotel only when
// project_checkout_enabled is false. A hotel can be on while System settings
// is off, so checkout_enabled alone never decides it.
export function checkoutOffForHotel(s: CheckoutSwitchState | null | undefined): boolean {
  return	!!s &&
			s.project_checkout_enabled === false;
}

// #91 §0.8 / §3.2 — the banner, or "" when the Checkout is on for the hotel.
// "…on this server." when the hotel follows a switched-off System settings,
// "…for this hotel." when the hotel itself is off.
export function checkoutOffBannerText(s: CheckoutSwitchState | null | undefined): string {
  if (!checkoutOffForHotel(s))
    return "";

  return	s!.checkout_enabled === false
			?	checkoutSwitchedOffText
			:	checkoutSwitchedOffHotelText;
}

// #91 §0.8 / §6.8 — the 409 checkout_disabled text for the latest read. When
// that read still says the Checkout is on, it changed meanwhile: the generic
// sentence, and the caller reads the table again.
export function checkoutDisabledText(s: CheckoutSwitchState | null | undefined): string {
  return checkoutOffBannerText(s) || checkoutSwitchedOffChangedText;
}

const errorTexts: { [code: string]: string } = {
  invalid_project_id:		genericBadRequest,
  invalid_body:				genericBadRequest,
  invalid_action:			genericBadRequest,
  invalid_checkout_id:		genericBadRequest,
  method_not_allowed:		genericBadRequest,
  body_too_large:			genericBadRequest,
  unsupported_media_type:	genericBadRequest,
  project_not_found:		"This hotel is not available to you.",
  room_not_found:			"This room does not exist any more.",
  no_tv:					"This room has no TV.",
  // §3.7 (rev 4) — one sentence for both, matching the action cell (§5.1).
  tv_update_needed:			deviceUnsupportedText + ".",
  tv_cannot_clear:			deviceUnsupportedText + ".",
  // §4.6 / §6.8 — the same sentence as the page banner.
  checkout_disabled:		checkoutSwitchedOffText,
  checkout_changed:			"This checkout was changed meanwhile. The row now shows the latest one.",
  not_cancellable:			"The TV has already taken this checkout, so it can no longer be cancelled.",
  project_disabled:			"This hotel is switched off, so its TVs cannot receive a checkout.",
  checkout_not_saved:		"The server could not save the checkout. Try again.",
  checkout_paused:			"Checkouts are paused on this server right now. Try again later.",
  project_reloading:		"The hotel is being saved right now. Try again in a moment.",
};

export const rateLimitedFallbackSeconds = 60;

// `unauthorized` is deliberately absent from the table: the session is gone
// and the login screen is the answer, not a sentence in a panel.
export function checkoutErrorText(code: string, retryAfter?: number): string {
  if (code === "rate_limited") {
    const secs =	retryAfter && retryAfter > 0
					?	Math.ceil(retryAfter)
					:	rateLimitedFallbackSeconds;

    return "Too many checkout requests. Try again in " + secs + " seconds.";
  }

  if (code === "unauthorized")
    return "";

  return errorTexts[code] || genericBadRequest;
}

// Retry-After is seconds here (never the HTTP-date form) — anything else falls
// back to the caller's default.
export function parseRetryAfter(header: string | null, fallback?: number): number {
  const n = header ? parseInt(header, 10) : NaN;

  if (	isFinite(n) &&
		n > 0
	 )
    return n;

  return fallback === undefined ? 0 : fallback;
}

// ---------------------------------------------------------------------------
// Normalisers. Every field is defended: for a housekeeping user this page is
// the whole console, and one missing member must not blank it.
// ---------------------------------------------------------------------------

function toNumber(v: any): number {
  return typeof v === "number" && isFinite(v) ? v : 0;
}

function toStr(v: any): string {
  return typeof v === "string" ? v : "";
}

export function normalizeLast(raw: any): CheckoutLast | null {
  if (	!raw ||
		typeof raw !== "object"
	 )
    return null;

  return {
    at:			toNumber(raw.at),
    result:		toStr(raw.result),
  };
}

export function normalizeRequest(raw: any): CheckoutRequest | null {
  if (	!raw ||
		typeof raw !== "object"
	 )
    return null;

  const out: CheckoutRequest = {
    id:				toStr(raw.id),
    state:			toStr(raw.state),
    requested_at:	toNumber(raw.requested_at),
    expires_at:		toNumber(raw.expires_at),
  };

  if (raw.acked_at !== undefined)	out.acked_at = toNumber(raw.acked_at);
  if (raw.ended_at !== undefined)	out.ended_at = toNumber(raw.ended_at);
  if (raw.reason !== undefined)		out.reason = toStr(raw.reason);

  return out;
}

export function normalizeRow(raw: any): CheckoutRoom | null {
  if (	!raw ||
		typeof raw !== "object"
	 )
    return null;

  return {
    room_no:		toStr(raw.room_no),
    name:			toStr(raw.name),
    tv:				toStr(raw.tv),
    last_checkout:	normalizeLast(raw.last_checkout),
    checkout:		normalizeRequest(raw.checkout),
  };
}

// §3.8 — the room-level carrier. Absent means "nothing to show yet", which is
// a different thing from a row with no checkout, so this answers null for it.
export function normalizeMwCheckout(raw: any): RoomMwCheckout | null {
  if (	!raw ||
		typeof raw !== "object"
	 )
    return null;

  return {
    tv:				toStr(raw.tv),
    last_checkout:	normalizeLast(raw.last_checkout),
    checkout:		normalizeRequest(raw.checkout),
  };
}

// The panel and the light page share every text helper, so the carrier is
// widened into a full row with the room's own number and name.
export function mwCheckoutToRow(roomNo: string, name: string, mw: RoomMwCheckout | null): CheckoutRoom | null {
  if (!mw)
    return null;

  return {
    room_no:		roomNo,
    name:			name,
    tv:				mw.tv,
    last_checkout:	mw.last_checkout,
    checkout:		mw.checkout,
  };
}

export function normalizeTable(body: any): CheckoutTable {
  const proj = body && body.project ? body.project : {};

  // One-room reads answer with `room`, the whole table with `rooms` — the page
  // only ever wants a list.
  const rows: CheckoutRoom[] = [];
  if (Array.isArray(body && body.rooms)) {
    for (const raw of body.rooms) {
      const row = normalizeRow(raw);
      if (row) rows.push(row);
    }
  } else {
    const row = normalizeRow(body ? body.room : null);
    if (row) rows.push(row);
  }

  // §0.1 compatibility — only an explicit false switches it off.
  const checkoutEnabled = !(body && body.checkout_enabled === false);

  return {
    project: {
      id:			toStr(proj.id),
      name:			toStr(proj.name),
      // Absent reads as ON: a hotel is only ever switched off explicitly, and
      // showing the "switched off" banner over a missing member would be worse.
      enabled:		proj.enabled !== false,
    },
    server_time:	toNumber(body ? body.server_time : 0),
    can_checkout:	!!(body && body.can_checkout),
    checkout_enabled:	checkoutEnabled,
    // #91 §0.8 — a boolean from 0.7.18ubuntu6 on; an older server sends none,
    // and there the System settings switch was the only one.
    project_checkout_enabled:	body && typeof body.project_checkout_enabled === "boolean"
    							?	body.project_checkout_enabled
    							:	checkoutEnabled,
    rooms:			rows,
  };
}

// ---------------------------------------------------------------------------
// Transport.
// ---------------------------------------------------------------------------

export function getCheckoutUrl(projectId: string, roomNo?: string): string {
  const params = new URLSearchParams();
  params.set("id", projectId);

  if (	roomNo &&
		roomNo !== ""
	 )
    params.set("room_no", roomNo);

  return checkoutUrl + "?" + params.toString();
}

// The status alone, for a body the server did not shape (a proxy's 502 page).
function codeForStatus(status: number): string {
  switch (status) {
  case 401:	return "unauthorized";
  case 404:	return "project_not_found";
  case 405:	return "method_not_allowed";
  case 413:	return "body_too_large";
  case 415:	return "unsupported_media_type";
  case 429:	return "rate_limited";
  case 500:	return "checkout_not_saved";
  case 503:	return "checkout_paused";
  default:	return "invalid_body";
  }
}

async function readError(response: Response, fallbackCode: string): Promise<CheckoutError> {
  let code = fallbackCode;
  let row: CheckoutRoom | null = null;

  try {
    const body = await response.json();
    const err =	body &&
				Array.isArray(body.errors) &&
				body.errors.length
				?	body.errors[0]
				:	null;

    if (err && typeof err.error === "string" && err.error !== "")
      code = err.error;

    if (err && err.result)
      row = normalizeRow(err.result);
  } catch {
    // An empty or non-JSON body leaves the status-derived fallback standing.
  }

  const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));

  if (response.status === 401)
    store.dispatchStatus(0);

  return {
    status:		response.status,
    code:		code,
    message:	checkoutErrorText(code, retryAfter),
    retryAfter:	retryAfter || undefined,
    row:		row,
  };
}

export async function fetchCheckout(projectId: string, roomNo?: string): Promise<CheckoutReadResult> {
  let response: Response;

  try {
    response = await fetch(getCheckoutUrl(projectId, roomNo), {
      method:		"GET",
      credentials:	"include",
      headers:		{ "Accept": "application/json" },
    });
  } catch (e) {
    console.error("fetchCheckout/failed:", e);
    return {
      kind:	"error",
      error: {
        status:		0,
        code:		"network",
        message:	"The server could not be reached. Trying again.",
      },
    };
  }

  if (!response.ok)
    return { kind: "error", error: await readError(response, codeForStatus(response.status)) };

  try {
    return { kind: "ok", table: normalizeTable(await response.json()) };
  } catch (e) {
    console.error("fetchCheckout/malformed body:", e);
    return {
      kind:	"error",
      error: {
        status:		response.status,
        code:		"invalid_body",
        message:	genericBadRequest,
      },
    };
  }
}

export interface CheckoutPostRequest {
  projectId:			string;
  roomNo:				string;
  action:				CheckoutAction;
  checkoutId?:			string;		// cancel only — the id the row shows
}

export async function postCheckout(req: CheckoutPostRequest): Promise<CheckoutPostResult> {
  // Unknown fields are refused, so the body carries checkout_id only for a
  // cancel.
  const body: any = {
    id:			req.projectId,
    room_no:	req.roomNo,
    action:		req.action,
  };

  if (req.action === "cancel")
    body.checkout_id = req.checkoutId || "";

  let response: Response;

  try {
    response = await fetch(checkoutUrl, {
      method:		"POST",
      credentials:	"include",
      // Required — the server answers 415 without it.
      headers:		{ "Content-Type": "application/json", "Accept": "application/json" },
      body:			JSON.stringify(body),
    });
  } catch (e) {
    console.error("postCheckout/failed:", e);
    return {
      kind:	"error",
      error: {
        status:		0,
        code:		"network",
        message:	"The server could not be reached. Try again.",
      },
    };
  }

  if (!response.ok)
    return { kind: "error", error: await readError(response, codeForStatus(response.status)) };

  try {
    const parsed = await response.json();
    const row = normalizeRow(parsed ? parsed.room : null);

    if (!row) {
      return {
        kind:	"error",
        error: {
          status:		response.status,
          code:		"invalid_body",
          message:	genericBadRequest,
        },
      };
    }

    return {
      kind:		"ok",
      // Only a `checkout` answer carries it; false means a request was already
      // waiting and the same one came back, so a double click is harmless.
      created:	parsed.created === true,
      room:		row,
    };
  } catch (e) {
    console.error("postCheckout/malformed body:", e);
    return {
      kind:	"error",
      error: {
        status:		response.status,
        code:		"invalid_body",
        message:	genericBadRequest,
      },
    };
  }
}
